import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  LineChart as RechartsLineChart,
  Line,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  ReferenceLine,
  Label,
} from "recharts";
import { MOCK_INSIGHTS_SERIES } from "../data/mockInsights";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { getSeriesColor as getSeriesColorUtil } from "../utils/chartColors";
import { formatNumber as formatCompactNumber } from "../../../../shared/utils/numberFormatter";

// Fallback palette when no config colors
const CHART_COLORS = [
  theme.accentChart || "#1d0c46",
  theme.primaryLight || "#4B57AA",
  "#8b5cf6",
  "#10b981",
  "#f59e0b",
  "#ec4899",
  "#6366f1",
  "#14b8a6",
];

export default function InsightsChart({ data, chartType = "line", config, height = 280, showLegend = true }) {
  const [showPieLegendMore, setShowPieLegendMore] = useState(false);
  const pieLegendMoreRef = useRef(null);
  const chartContainerRef = useRef(null);

  // Close "+X More" dropdown when clicking outside
  useEffect(() => {
    if (!showPieLegendMore) return;
    const handleClickOutside = (e) => {
      if (pieLegendMoreRef.current && !pieLegendMoreRef.current.contains(e.target)) {
        setShowPieLegendMore(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPieLegendMore]);
  // Check if series is already normalized (has breakdownKey property)
  const isAlreadyNormalized = data?.series && 
                              Array.isArray(data.series) && 
                              data.series.length > 0 && 
                              data.series[0].breakdownKey !== undefined;
  
  // Check if this is breakdown format (breakdowns array exists)
  const isBreakdownFormat = !isAlreadyNormalized &&
                            data?.series && 
                            Array.isArray(data.series) && 
                            data.series.length > 0 && 
                            data.series[0].breakdowns && 
                            Array.isArray(data.series[0].breakdowns) &&
                            data.series[0].breakdowns.length > 0;
  
  // Normalize breakdown format to multi-series format
  let normalizedSeries = [];
  if (isAlreadyNormalized) {
    // Series is already normalized (sorted and limited by parent component)
    normalizedSeries = data.series;
  } else if (isBreakdownFormat) {
    // Check if there are multiple metrics (need to include metric name in labels)
    const hasMultipleMetrics = data?.series && data.series.length > 1;
    
    // Flatten breakdowns: each breakdown becomes a separate series
    data.series.forEach((metricSeries) => {
      if (metricSeries.breakdowns && Array.isArray(metricSeries.breakdowns)) {
        metricSeries.breakdowns.forEach((breakdown, index) => {
          // Build breakdown label
          const breakdownValueLabel = breakdown.breakdownKey || 
                                     (breakdown.breakdownValues && breakdown.breakdownValues.join(' / ')) ||
                                     `Breakdown ${index + 1}`;
          
          // If multiple metrics, prefix with metric name to distinguish
          const breakdownLabel = hasMultipleMetrics 
            ? `${metricSeries.metricName} - ${breakdownValueLabel}`
            : breakdownValueLabel;
          
          normalizedSeries.push({
            metricId: metricSeries.metricId,
            metricName: breakdownLabel,
            data: breakdown.data || [],
            summary: {
              total: breakdown.total || 0,
              ...(metricSeries.summary || {})
            },
            breakdownKey: breakdown.breakdownKey,
            breakdownValues: breakdown.breakdownValues
          });
        });
      }
    });
  } else {
    normalizedSeries = data?.series || [];
  }
  
  // Support both old format (single series array) and new format (multiple series)
  const isMultiSeries = normalizedSeries.length > 0 && normalizedSeries[0].data;
  const series = isMultiSeries ? normalizedSeries : (normalizedSeries.length > 0 ? normalizedSeries : MOCK_INSIGHTS_SERIES.series);
  const metricsWithAxis = data?.metrics && Array.isArray(data.metrics) ? data.metrics : null;
  const getAxisForIndex = (i) => (metricsWithAxis && metricsWithAxis[i] ? (metricsWithAxis[i].axis || "left") : "left");
  const configColors = config?.colors && Array.isArray(config.colors) ? config.colors : null;
  const getSeriesColor = (index) => getSeriesColorUtil(index, configColors || CHART_COLORS);
  const leftYConfig = config?.axis?.leftY ?? {};
  const rightYConfig = config?.axis?.rightY ?? {};
  const xAxisConfig = config?.axis?.xAxis ?? {};
  const leftYLabel = leftYConfig.label;
  const rightYLabel = rightYConfig.label;
  const xAxisLabel = xAxisConfig.label;
  
  // Only use mock summary if data is completely missing (for demo/empty states)
  // For multi-series or when summary is intentionally undefined, don't fall back to mock
  const summary = data?.summary !== undefined ? data.summary : (data?.series ? null : MOCK_INSIGHTS_SERIES.summary);

  // Normalize chartType to handle any whitespace or case issues
  const normalizedChartType = String(chartType || "line").toLowerCase().trim();
  const showChartLegend = showLegend && normalizedChartType === "pie";

  // Table and horizontal bar charts show aggregated totals
  // Column charts (vertical) show date-wise data (like Mixpanel)
  const isTable = normalizedChartType === 'table';
  const isHorizontalBar = ['bar', 'stacked-bar'].includes(normalizedChartType);
  const isColumnChart = ['column', 'stacked-column'].includes(normalizedChartType);
  const isBarChart = isHorizontalBar || isColumnChart;

  // Transform multi-series data into chart format
  let chartData = [];
  let seriesConfig = [];
  
  if (isMultiSeries) {
    if (isTable || isHorizontalBar) {
      // For table and horizontal bar charts: use summary totals (not sum of daily values)
      // This ensures we use the correct totals from backend (e.g., distinct counts for uniques/sessions)
      series.forEach((metricSeries, index) => {
        const metricKey = `metric_${index}`;
        const metricName = metricSeries.metricName || `Metric ${String.fromCharCode(65 + index)}`;
        const color = getSeriesColor(index);
        
        // Use summary.total if available (correct total from backend), otherwise fallback to sum
        const total = metricSeries.summary?.total ?? 
                     (metricSeries.data || []).reduce((sum, point) => sum + (point.value || 0), 0);
        
        seriesConfig.push({
          key: metricKey,
          name: metricName,
          color: color,
          total: total
        });
      });
      
      // Create single data point with all metric totals
      const barDataPoint = { name: 'Total' };
      seriesConfig.forEach(config => {
        barDataPoint[config.key] = config.total;
      });
      chartData = [barDataPoint];
    } else {
      // For line/area/column charts: merge all series by date (date-wise data)
      const dateMap = new Map();
      
      series.forEach((metricSeries, index) => {
        const metricKey = `metric_${index}`;
        const metricName = metricSeries.metricName || `Metric ${String.fromCharCode(65 + index)}`;
        const color = getSeriesColor(index);
        const axis = getAxisForIndex(index);
        seriesConfig.push({
          key: metricKey,
          name: metricName,
          color: color,
          axis,
          data: metricSeries.data || []
        });
        
        (metricSeries.data || []).forEach(point => {
          if (!dateMap.has(point.date)) {
            dateMap.set(point.date, { date: point.date });
          }
          dateMap.get(point.date)[metricKey] = point.value || 0;
        });
      });
      
      // Fill missing values with 0 for all metrics to prevent broken lines
      chartData = Array.from(dateMap.values()).sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      ).map(dataPoint => {
        // Ensure all metrics have a value (default to 0 if missing)
        seriesConfig.forEach(config => {
          if (dataPoint[config.key] === undefined) {
            dataPoint[config.key] = 0;
          }
        });
        return dataPoint;
      });
    }
  } else {
    // Legacy single series format
    if (isTable || isHorizontalBar) {
      // For table and horizontal bar charts: use summary.total if available (correct total from backend)
      // Otherwise fallback to sum of daily values
      const total = summary?.total ?? 
                   (series || []).reduce((sum, point) => sum + (point.value || 0), 0);
      chartData = [{ name: 'Total', value: total }];
      seriesConfig = [{
        key: 'value',
        name: 'Value',
        color: getSeriesColor(0),
        total: total
      }];
    } else {
      // For line/column charts: use date-wise data
      chartData = series;
      seriesConfig = [{
        key: 'value',
        name: 'Value',
        color: getSeriesColor(0),
        data: series
      }];
    }
  }

  // Ensure we have data
  if (!Array.isArray(chartData) || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-sm" style={{ color: theme.textMuted }}>
          No data available
        </div>
      </div>
    );
  }

  const getFormatForDataKey = (dataKey) => {
    const sc = seriesConfig.find((s) => s.key === dataKey);
    return sc?.axis === "right" ? formatValueRight : formatValue;
  };

  const tooltipContent = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    const getActualValue = (entry) => (row && row[`__actual_${entry.dataKey}`] !== undefined) ? row[`__actual_${entry.dataKey}`] : entry.value;
    return (
      <div
        className="px-3 py-2 rounded border shadow-sm text-sm"
        style={{
          backgroundColor: theme.bgRaised,
          borderColor: theme.border,
          color: theme.textPrimary,
        }}
      >
        <div className="mb-1" style={{ color: theme.textMuted }}>{formatDate(label)}</div>
        {payload.length === 1 ? (
          <div className="font-medium">
            {(() => {
              const actual = getActualValue(payload[0]);
              return actual == null ? "(hidden)" : getFormatForDataKey(payload[0].dataKey)(actual);
            })()}
          </div>
        ) : (
          <>
            {payload.map((entry, index) => {
              const fmt = getFormatForDataKey(entry.dataKey);
              const actual = getActualValue(entry);
              return (
                <div key={index} className="flex items-center gap-2 mb-1">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span style={{ color: theme.textSecondary }}>
                    {entry.name || `Series ${index + 1}`}: {actual == null ? "(hidden)" : fmt(actual)}
                  </span>
                </div>
              );
            })}
            {isMultiSeries && !hasDualAxis && payload.length > 0 && (() => {
              const actualSum = payload.reduce((sum, p) => sum + (getActualValue(p) ?? 0), 0);
              if (payload.every((p) => getActualValue(p) != null)) {
                return (
                  <div className="font-medium mt-1 pt-1 border-t" style={{ borderColor: theme.border }}>
                    Total: {formatValue(actualSum)}
                  </div>
                );
              }
              return null;
            })()}
          </>
        )}
        {summary?.delta && (
          <div className="text-xs mt-0.5" style={{ color: theme.accentPositive }}>{summary.delta}</div>
        )}
        {hasThresholds && (
          <div className="text-xs mt-0.5" style={{ color: theme.textMuted }}>
            Threshold bounds affect only the chart; tables and data show actual values.
          </div>
        )}
      </div>
    );
  };

  // Portal tooltip so it renders at document body and isn't clipped by chart container overflow
  const makePortalTooltip = (contentFn) => (props) => {
    const content = contentFn(props);
    if (!content) return null;
    const rect = chartContainerRef.current?.getBoundingClientRect();
    const coord = props.coordinate || {};
    const x = rect ? rect.left + (coord.x ?? 0) : (coord.x ?? 0);
    const y = rect ? rect.top + (coord.y ?? 0) : (coord.y ?? 0);
    const left = x + 12;
    const top = y - 8;
    return ReactDOM.createPortal(
      <div className="fixed pointer-events-none z-[9999]" style={{ left: `${left}px`, top: `${top}px` }}>
        {content}
      </div>,
      document.body
    );
  };
  const portalTooltipContent = makePortalTooltip(tooltipContent);

  const gridStroke = "rgba(0,0,0,0.12)";
  const axisStroke = "rgba(0,0,0,0.15)";
  const tickFill = theme.textMuted;
  const axisLabelStyle = { fontSize: 12, fontWeight: 500, fill: "#6B7280" };

  const formatDate = (v) => {
    if (!v) return "";
    try {
      // Handle ISO date strings like "2026-01-14T00:00:00.000Z" or "01-14T00:00:00.000Z"
      const dateStr = typeof v === 'string' ? v : String(v);
      
      // Extract date part (before 'T' if present)
      const datePart = dateStr.split('T')[0];
      
      // Parse the date
      let date;
      if (datePart.includes('-')) {
        // Full ISO format: "2026-01-14" or partial: "01-14"
        const parts = datePart.split('-');
        if (parts.length === 3) {
          // Full date: "2026-01-14"
          date = new Date(`${parts[0]}-${parts[1]}-${parts[2]}`);
        } else if (parts.length === 2) {
          // Partial: "01-14" - assume current year
          const currentYear = new Date().getFullYear();
          date = new Date(`${currentYear}-${parts[0]}-${parts[1]}`);
        } else {
          return datePart; // Fallback
        }
      } else {
        date = new Date(dateStr);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return datePart; // Fallback to original if parsing fails
      }
      
      // Format as "Jan 14" (short month + day)
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const day = date.getDate();
      return `${month} ${day}`;
    } catch (e) {
      // Fallback: try to extract just the date part
      const datePart = String(v).split('T')[0];
      return datePart.includes('-') ? datePart.split('-').slice(-2).join('-') : datePart;
    }
  };
  const decimals = leftYConfig.decimals ?? ".0";
  const decimalPlaces = decimals === ".00" ? 2 : 0;
  const unit = leftYConfig.unit ?? "#";
  const customUnit = leftYConfig.customUnit ?? "";
  const useAbbreviation = leftYConfig.abbreviation !== false;
  const prefix = unit === "$" ? "$" : unit === "%" ? "" : unit === "Custom" ? "" : "";
  const suffix = unit === "%" ? "%" : unit === "Custom" ? (customUnit || "") : "";
  const formatValue = (value) => {
    const num = Number(value);
    if (isNaN(num)) return "0";
    const str = useAbbreviation ? formatCompactNumber(num, decimalPlaces || 1) : Number(num).toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${prefix}${str}${suffix}`;
  };
  const formatValueRight = (value) => {
    const dec = rightYConfig.decimals ?? ".0";
    const dp = dec === ".00" ? 2 : 0;
    const u = rightYConfig.unit ?? "#";
    const cu = rightYConfig.customUnit ?? "";
    const useAbbr = rightYConfig.abbreviation !== false;
    const pre = u === "$" ? "$" : u === "%" ? "" : u === "Custom" ? "" : "";
    const suf = u === "%" ? "%" : u === "Custom" ? (cu || "") : "";
    const num = Number(value);
    if (isNaN(num)) return "0";
    const str = useAbbr ? formatCompactNumber(num, dp || 1) : Number(num).toFixed(dp).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${pre}${str}${suf}`;
  };
  // Parse threshold values (presentation-only; never affects aggregation)
  const getThresholdValues = (axisConfig) => {
    const minRaw = axisConfig.thresholdMin != null && String(axisConfig.thresholdMin).trim() !== "" ? axisConfig.thresholdMin : null;
    const maxRaw = axisConfig.thresholdMax != null && String(axisConfig.thresholdMax).trim() !== "" ? axisConfig.thresholdMax : null;
    const minNum = minRaw != null ? parseFloat(minRaw) : NaN;
    const maxNum = maxRaw != null ? parseFloat(maxRaw) : NaN;
    return { min: !isNaN(minNum) ? minNum : null, max: !isNaN(maxNum) ? maxNum : null };
  };
  const { min: minThreshold, max: maxThreshold } = getThresholdValues(leftYConfig);
  const { min: minThresholdRight, max: maxThresholdRight } = getThresholdValues(rightYConfig);
  const hasThresholds = minThreshold != null || maxThreshold != null;
  const hasThresholdsRight = minThresholdRight != null || maxThresholdRight != null;
  const leftYScale = leftYConfig.scale === "log" ? "log" : "linear";
  const rightYScale = rightYConfig.scale === "log" ? "log" : "linear";

  const thresholdLines = [];
  if (minThreshold != null && (leftYScale !== "log" || minThreshold > 0)) {
    thresholdLines.push({ value: minThreshold, stroke: "#94a3b8", strokeDasharray: "5 5", strokeWidth: 1.5, yAxisId: "left" });
  }
  if (maxThreshold != null && maxThreshold !== minThreshold && (leftYScale !== "log" || maxThreshold > 0)) {
    thresholdLines.push({ value: maxThreshold, stroke: "#94a3b8", strokeDasharray: "5 5", strokeWidth: 1.5, yAxisId: "left" });
  }
  if (minThresholdRight != null && (rightYScale !== "log" || minThresholdRight > 0)) {
    thresholdLines.push({ value: minThresholdRight, stroke: "#94a3b8", strokeDasharray: "5 5", strokeWidth: 1.5, yAxisId: "right" });
  }
  if (maxThresholdRight != null && maxThresholdRight !== minThresholdRight && (rightYScale !== "log" || maxThresholdRight > 0)) {
    thresholdLines.push({ value: maxThresholdRight, stroke: "#94a3b8", strokeDasharray: "5 5", strokeWidth: 1.5, yAxisId: "right" });
  }

  // Linear: [min(values), max(values)]. Log: [min(positiveValues), max(positiveValues)] — no padding to 0.
  const getDomainFromValues = (data, keys, scaleType = "linear") => {
    let lo = Infinity;
    let hi = -Infinity;
    data.forEach((row) => {
      keys.forEach((k) => {
        const v = row[k];
        if (v != null && typeof v === "number" && !isNaN(v)) {
          if (scaleType === "log" && v <= 0) return;
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
      });
    });
    if (lo === Infinity || hi === -Infinity) return undefined;
    return [lo, hi];
  };

  // Tight domain; log scale uses only positive values and does not pad.
  const computeTightDomain = (data, keys, scaleType) => {
    const values = [];
    data.forEach((row) => {
      keys.forEach((k) => {
        const v = row[k];
        if (v != null && typeof v === "number" && !isNaN(v)) values.push(v);
      });
    });
    if (values.length === 0) return undefined;
    if (scaleType === "log") {
      const positive = values.filter((v) => v > 0);
      if (positive.length === 0) return undefined;
      const lo = Math.min(...positive);
      const hi = Math.max(...positive);
      return [lo, hi];
    }
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    return lo === hi ? [lo, hi + 1] : [lo, hi];
  };

  // Stacked charts: axis max must be based on cumulative sum per data point, not max individual series.
  // For each row, sum all keys; axis max = max(cumulative sums) * paddingFactor (e.g. 1.1).
  const getStackedDomainFromValues = (data, keys, scaleType = "linear", paddingFactor = 1.1) => {
    if (!data?.length || !keys?.length) return undefined;
    const stackedSums = data.map((row) => {
      let sum = 0;
      keys.forEach((k) => {
        const v = row[k];
        if (v != null && typeof v === "number" && !isNaN(v)) sum += v;
      });
      return sum;
    });
    if (scaleType === "log") {
      const positive = stackedSums.filter((s) => s > 0);
      if (positive.length === 0) return undefined;
      const lo = Math.min(...positive);
      const hi = Math.max(...positive);
      return [lo, hi * paddingFactor];
    }
    const maxStacked = Math.max(...stackedSums);
    const minStacked = Math.min(...stackedSums);
    // Stacked charts typically use 0 as base; allow negative if data has negatives
    const lo = minStacked >= 0 ? 0 : minStacked;
    const hi = maxStacked <= 0 ? 0 : maxStacked * paddingFactor;
    if (lo === hi && maxStacked === 0) return [0, 1];
    return [lo, hi];
  };

  const valueKeys = seriesConfig.map((s) => s.key);
  const leftSeriesConfig = seriesConfig.filter((s) => (s.axis || "left") === "left");
  const rightSeriesConfig = seriesConfig.filter((s) => s.axis === "right");
  const leftKeys = leftSeriesConfig.map((s) => s.key);
  const rightKeys = rightSeriesConfig.map((s) => s.key);
  const hasDualAxis = leftKeys.length > 0 && rightKeys.length > 0;
  const outOfBoundsLeft = leftYConfig.outOfBounds === "visible";
  const outOfBoundsRight = rightYConfig.outOfBounds === "visible";

  // Clamp value to range (for threshold bounds); preserves null/NaN
  const clampValue = (v, lo, hi) => (v == null || typeof v !== "number" || isNaN(v)) ? v : Math.max(lo, Math.min(hi, v));

  // When Hidden: domain clamping + allowDataOverflow clips the area visually; data is never modified
  // Apply threshold bounds: Visible => clamp to bounds for display; Hidden => null outside bounds. Store __actual_* for tooltip.
  let displayData = chartData;
  if (leftYScale === "log") {
    displayData = displayData.map((row) => {
      const out = { ...row };
      leftKeys.forEach((k) => {
        const v = out[k];
        if (v != null && typeof v === "number" && !isNaN(v) && v <= 0) out[k] = null;
      });
      return out;
    });
  }
  if (rightYScale === "log") {
    displayData = displayData.map((row) => {
      const out = { ...row };
      rightKeys.forEach((k) => {
        const v = out[k];
        if (v != null && typeof v === "number" && !isNaN(v) && v <= 0) out[k] = null;
      });
      return out;
    });
  }
  if (hasThresholds && leftKeys.length > 0) {
    const lo = minThreshold ?? -Infinity;
    const hi = maxThreshold ?? Infinity;
    displayData = displayData.map((row) => {
      const out = { ...row };
      leftKeys.forEach((k) => {
        const actual = row[k];
        out[`__actual_${k}`] = actual;
        if (actual == null || (typeof actual === "number" && isNaN(actual))) return;
        if (outOfBoundsLeft) {
          out[k] = clampValue(actual, lo, hi);
        } else {
          out[k] = (actual >= lo && actual <= hi) ? actual : null;
        }
      });
      return out;
    });
  }
  if (hasThresholdsRight && rightKeys.length > 0) {
    const loR = minThresholdRight ?? -Infinity;
    const hiR = maxThresholdRight ?? Infinity;
    displayData = displayData.map((row) => {
      const out = { ...row };
      rightKeys.forEach((k) => {
        const actual = row[k];
        out[`__actual_${k}`] = actual;
        if (actual == null || (typeof actual === "number" && isNaN(actual))) return;
        if (outOfBoundsRight) {
          out[k] = clampValue(actual, loR, hiR);
        } else {
          out[k] = (actual >= loR && actual <= hiR) ? actual : null;
        }
      });
      return out;
    });
  }

  // Y-axis domain: when threshold bounds are set, axis scale is restricted to [min, max] only (Mixpanel-style)
  let leftYDomain = undefined;
  if (leftKeys.length > 0) {
    const dataDomainLeft = getDomainFromValues(chartData, leftKeys, leftYScale);
    if (!dataDomainLeft && (hasThresholds || leftYScale === "log")) {
      if (leftYScale === "log") {
        const ptMin = minThreshold != null && minThreshold > 0 ? minThreshold : null;
        const ptMax = maxThreshold != null && maxThreshold > 0 ? maxThreshold : null;
        leftYDomain = ptMin != null && ptMax != null ? [Math.min(ptMin, ptMax), Math.max(ptMin, ptMax)] : ptMin != null ? [ptMin, ptMin * 10] : ptMax != null ? [ptMax / 10, ptMax] : undefined;
      } else {
        leftYDomain = [minThreshold ?? 0, maxThreshold ?? 100];
      }
    } else {
      leftYDomain = dataDomainLeft;
    }
    if (leftYDomain && hasThresholds) {
      const thrMin = leftYScale === "log" ? (minThreshold != null && minThreshold > 0 ? minThreshold : null) : minThreshold;
      const thrMax = leftYScale === "log" ? (maxThreshold != null && maxThreshold > 0 ? maxThreshold : null) : maxThreshold;
      leftYDomain = [
        thrMin != null ? thrMin : leftYDomain[0],
        thrMax != null ? thrMax : leftYDomain[1]
      ];
    }
  }

  let rightYDomain = undefined;
  if (rightKeys.length > 0) {
    const dataDomainRight = getDomainFromValues(chartData, rightKeys, rightYScale);
    if (!dataDomainRight && (hasThresholdsRight || rightYScale === "log")) {
      if (rightYScale === "log") {
        const ptMin = minThresholdRight != null && minThresholdRight > 0 ? minThresholdRight : null;
        const ptMax = maxThresholdRight != null && maxThresholdRight > 0 ? maxThresholdRight : null;
        rightYDomain = ptMin != null && ptMax != null ? [Math.min(ptMin, ptMax), Math.max(ptMin, ptMax)] : ptMin != null ? [ptMin, ptMin * 10] : ptMax != null ? [ptMax / 10, ptMax] : undefined;
      } else {
        rightYDomain = [minThresholdRight ?? 0, maxThresholdRight ?? 100];
      }
    } else {
      rightYDomain = dataDomainRight;
    }
    if (rightYDomain && hasThresholdsRight) {
      const thrMin = rightYScale === "log" ? (minThresholdRight != null && minThresholdRight > 0 ? minThresholdRight : null) : minThresholdRight;
      const thrMax = rightYScale === "log" ? (maxThresholdRight != null && maxThresholdRight > 0 ? maxThresholdRight : null) : maxThresholdRight;
      rightYDomain = [
        thrMin != null ? thrMin : rightYDomain[0],
        thrMax != null ? thrMax : rightYDomain[1]
      ];
    }
  }

  const leftYVisible = leftKeys.length > 0 && leftYConfig.visible !== false;
  const rightYVisible = rightKeys.length > 0 && rightYConfig.visible !== false;
  const xAxisVisible = xAxisConfig.visible !== false;
  // Only column and line charts render a right Y-axis; stacked-line/stacked-column never do
  const hasRightAxis = (normalizedChartType === "column" || normalizedChartType === "line") && rightYVisible;
  const visibleThresholdLines = thresholdLines.filter((line) => {
    const aid = line.yAxisId || "left";
    return aid === "left" || (aid === "right" && hasRightAxis);
  });
  const connectNullsLeft = outOfBoundsLeft || !hasThresholds;
  const connectNullsRight = outOfBoundsRight || !hasThresholdsRight;

  if (normalizedChartType === "column" || normalizedChartType === "stacked-column") {
    const tightLeft = leftKeys.length > 0
      ? (normalizedChartType === "stacked-column"
          ? getStackedDomainFromValues(chartData, leftKeys, leftYScale, 1.1)
          : computeTightDomain(chartData, leftKeys, leftYScale))
      : undefined;
    const tightRight = rightKeys.length > 0 ? computeTightDomain(chartData, rightKeys, rightYScale) : undefined;
    if (tightLeft) {
      if (hasThresholds) {
        const incMin = leftYScale === "log" ? (minThreshold != null && minThreshold > 0 ? minThreshold : null) : minThreshold;
        const incMax = leftYScale === "log" ? (maxThreshold != null && maxThreshold > 0 ? maxThreshold : null) : maxThreshold;
        leftYDomain = [incMin != null ? incMin : tightLeft[0], incMax != null ? incMax : tightLeft[1]];
      } else {
        leftYDomain = tightLeft;
      }
    }
    if (tightRight) {
      if (hasThresholdsRight) {
        const incMin = rightYScale === "log" ? (minThresholdRight != null && minThresholdRight > 0 ? minThresholdRight : null) : minThresholdRight;
        const incMax = rightYScale === "log" ? (maxThresholdRight != null && maxThresholdRight > 0 ? maxThresholdRight : null) : maxThresholdRight;
        rightYDomain = [incMin != null ? incMin : tightRight[0], incMax != null ? incMax : tightRight[1]];
      } else {
        rightYDomain = tightRight;
      }
    }
  } else if (normalizedChartType === "line" || normalizedChartType === "stacked-line") {
    if (leftKeys.length > 0 && !hasThresholds) {
      if (normalizedChartType === "stacked-line") {
        leftYDomain = getStackedDomainFromValues(chartData, leftKeys, leftYScale, 1.1);
      } else if (!leftYDomain) {
        leftYDomain = computeTightDomain(chartData, leftKeys, leftYScale);
      }
    }
    if (rightKeys.length > 0 && !hasThresholdsRight) {
      if (normalizedChartType === "stacked-line") {
        rightYDomain = getStackedDomainFromValues(chartData, rightKeys, rightYScale, 1.1);
      } else if (!rightYDomain) {
        rightYDomain = computeTightDomain(chartData, rightKeys, rightYScale);
      }
    }
  } else if (normalizedChartType === "stacked-bar") {
    if (leftKeys.length > 0) {
      leftYDomain = getStackedDomainFromValues(chartData, leftKeys, leftYScale, 1.1);
    }
  }

  const hasYAxisLabel = leftYLabel && String(leftYLabel).trim();
  const hasRightYAxisLabel = rightYLabel && String(rightYLabel).trim();
  const yAxisWidth = 38;
  const chartLeft = yAxisWidth;
  // Right margin slightly larger to balance visual weight of left Y-axis labels/ticks
  const chartRight = rightYVisible ? yAxisWidth : 50;
  const hasXAxisLabel = xAxisLabel && String(xAxisLabel).trim();
  const chartBottom = hasXAxisLabel ? 40 : 24; // Padding for X-axis ticks; extra when label present
  const chartMargin = { top: 2, right: chartRight, left: chartLeft, bottom: chartBottom };
  const columnChartMargin = { top: 2, right: chartRight, left: chartLeft, bottom: chartBottom };
  const yAxisLabelProps = hasYAxisLabel
    ? {
        label: (
          <Label
            value={leftYLabel}
            angle={-90}
            position="insideLeft"
            dx={-6}
            style={{ fontSize: 12, fontWeight: 500, fill: "#6B7280", fontFamily: "Raleway, sans-serif" }}
          />
        ),
      }
    : {};
  const rightYAxisLabelProps = hasRightYAxisLabel
    ? {
        label: (
          <Label
            value={rightYLabel}
            angle={90}
            position="insideRight"
            dx={6}
            style={{ fontSize: 12, fontWeight: 500, fill: "#6B7280", fontFamily: "Raleway, sans-serif" }}
          />
        ),
      }
    : {};
  const leftYAxisTick = { fill: tickFill, fontSize: 11, dx: 0 };
  const rightYAxisTick = { fill: tickFill, fontSize: 11, dx: 0 };
  const YAxisLabelOverlay = null;
  const xAxisLabelProps = hasXAxisLabel
    ? {
        label: (
          <Label
            value={xAxisLabel}
            position="insideBottom"
            dy={16}
            style={{ fontSize: 12, fontWeight: 500, fill: "#6B7280", fontFamily: "Raleway, sans-serif" }}
          />
        ),
      }
    : {};

  // Stacked Line Chart
  if (normalizedChartType === "stacked-line") {
    return (
      <div ref={chartContainerRef} className="relative w-full" style={{ height, margin: 0, padding: 0 }}>
        {YAxisLabelOverlay}
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={displayData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
            <XAxis dataKey="date" tick={{ fill: tickFill, fontSize: 11 }} axisLine={{ stroke: axisStroke }} tickLine={false} tickFormatter={formatDate} hide={!xAxisVisible} {...xAxisLabelProps} />
            <YAxis yAxisId="left" hide={!leftYVisible} tick={leftYAxisTick} axisLine={false} tickLine={false} width={leftYVisible ? yAxisWidth : 0} tickFormatter={formatValue} domain={leftYDomain} scale={leftYScale} allowDataOverflow={!outOfBoundsLeft} {...yAxisLabelProps} />
            {visibleThresholdLines.map((line, i) => (
              <ReferenceLine key={`threshold-${i}`} yAxisId={line.yAxisId || "left"} y={line.value} stroke={line.stroke} strokeDasharray={line.strokeDasharray} strokeWidth={line.strokeWidth} />
            ))}
            <Tooltip content={portalTooltipContent} />
            {showChartLegend && (
              <Legend
                verticalAlign="top"
                align="center"
                layout="horizontal"
                wrapperStyle={{ paddingBottom: 8 }}
                formatter={(value) => {
                  const config = seriesConfig.find(s => s.key === value);
                  return config?.name || value;
                }}
              />
            )}
            {seriesConfig.map((config, index) => (
              <Area
                key={config.key}
                yAxisId="left"
                type="monotone"
                dataKey={config.key}
                stackId="stack"
                stroke={config.color}
                strokeWidth={2}
                fill={config.color}
                fillOpacity={0.6}
                dot={false}
                activeDot={{ r: 4, fill: config.color }}
                name={config.name}
                connectNulls={connectNullsLeft}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Column Chart (vertical bars) - shows date-wise data with one bar per metric per date
  if (normalizedChartType === "column") {
    return (
      <div ref={chartContainerRef} className="relative w-full" style={{ height, margin: 0, padding: 0 }}>
        {YAxisLabelOverlay}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={displayData} 
            margin={columnChartMargin}
            barCategoryGap="20%"
            barGap={4}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
            <XAxis dataKey="date" tick={{ fill: tickFill, fontSize: 11 }} axisLine={{ stroke: axisStroke }} tickLine={false} tickFormatter={formatDate} hide={!xAxisVisible} {...xAxisLabelProps} />
            <YAxis yAxisId="left" hide={!leftYVisible} tick={leftYAxisTick} axisLine={false} tickLine={false} width={leftYVisible ? yAxisWidth : 0} tickFormatter={formatValue} domain={leftYDomain} scale={leftYScale} tickCount={5} allowDataOverflow={!outOfBoundsLeft} {...yAxisLabelProps} />
            {rightYVisible && (
              <YAxis yAxisId="right" orientation="right" hide={false} tick={rightYAxisTick} axisLine={false} tickLine={false} width={yAxisWidth} tickFormatter={formatValueRight} domain={rightYDomain} scale={rightYScale} tickCount={5} allowDataOverflow={!outOfBoundsRight} {...rightYAxisLabelProps} />
            )}
            {visibleThresholdLines.map((line, i) => (
              <ReferenceLine key={`threshold-${i}`} yAxisId={line.yAxisId || "left"} y={line.value} stroke={line.stroke} strokeDasharray={line.strokeDasharray} strokeWidth={line.strokeWidth} />
            ))}
            <Tooltip content={portalTooltipContent} cursor={{ fill: `${theme.primaryLight}20` }} />
            {showChartLegend && (
              <Legend
                verticalAlign="top"
                align="center"
                layout="horizontal"
                wrapperStyle={{ paddingBottom: 8 }}
                formatter={(value) => {
                  const sc = seriesConfig.find(s => s.key === value);
                  return sc?.name || value;
                }}
              />
            )}
            {seriesConfig.map((config) => (
              <Bar key={config.key} yAxisId={config.axis === "right" ? "right" : "left"} dataKey={config.key} fill={config.color} radius={[4, 4, 0, 0]} isAnimationActive={true} name={config.name} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Stacked Column Chart (vertical stacked) - shows date-wise data with stacked bars per date
  if (normalizedChartType === "stacked-column") {
    return (
      <div ref={chartContainerRef} className="relative w-full" style={{ height }}>
        {YAxisLabelOverlay}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={displayData} 
            margin={columnChartMargin}
            barCategoryGap="20%"
            barGap={4}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
            <XAxis dataKey="date" tick={{ fill: tickFill, fontSize: 11 }} axisLine={{ stroke: axisStroke }} tickLine={false} tickFormatter={formatDate} hide={!xAxisVisible} {...xAxisLabelProps} />
            <YAxis yAxisId="left" hide={!leftYVisible} tick={leftYAxisTick} axisLine={false} tickLine={false} width={leftYVisible ? yAxisWidth : 0} tickFormatter={formatValue} domain={leftYDomain} scale={leftYScale} tickCount={5} allowDataOverflow={!outOfBoundsLeft} {...yAxisLabelProps} />
            {visibleThresholdLines.map((line, i) => (
              <ReferenceLine key={`threshold-${i}`} yAxisId={line.yAxisId || "left"} y={line.value} stroke={line.stroke} strokeDasharray={line.strokeDasharray} strokeWidth={line.strokeWidth} />
            ))}
            <Tooltip content={portalTooltipContent} cursor={{ fill: `${theme.primaryLight}20` }} />
            {showChartLegend && (
              <Legend
                verticalAlign="top"
                align="center"
                layout="horizontal"
                wrapperStyle={{ paddingBottom: 8 }}
                formatter={(value) => {
                  const config = seriesConfig.find(s => s.key === value);
                  return config?.name || value;
                }}
              />
            )}
            {seriesConfig.map((config, idx) => (
              <Bar key={config.key} yAxisId="left" dataKey={config.key} stackId="stack" fill={config.color} radius={idx === seriesConfig.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} name={config.name} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Bar Chart (horizontal bars) - shows aggregated totals, one bar per metric
  // With hierarchical breakdowns: shows nested groups with bars only at leaf level
  // Stacked bar: same structure but with stacked segments at leaf level
  if (normalizedChartType === "bar" || normalizedChartType === "stacked-bar") {
    // Extract breakdown depth safely (avoids repeated property access and undefined crashes)
    const breakdownDepth = series?.[0]?.breakdownValues?.length || 0;
    
    // Check if we have hierarchical breakdowns (chart-type dependent)
    // BAR charts: hierarchical for single or multiple breakdowns (shows Metric | Country | Value)
    // STACKED bars: hierarchical ONLY for multiple breakdowns (single breakdown uses flat renderer)
    const hasHierarchicalBreakdowns = isMultiSeries && 
      (
        (normalizedChartType === "bar" && breakdownDepth >= 1) ||
        (normalizedChartType === "stacked-bar" && breakdownDepth >= 1)
      );

    // Recursive function to build hierarchical tree dynamically (level 0 → N)
    const buildHierarchicalTree = (seriesData, levelIndex, maxLevel) => {
      if (levelIndex >= maxLevel) {
        // Leaf level - return bars
        return seriesData.map(item => ({
          label: item.breakdownValues[item.breakdownValues.length - 1],
          value: item.summary?.total ?? (item.data || []).reduce((sum, point) => sum + (point.value || 0), 0),
          series: item
        }));
      }

      // Group by current level value
      const groups = new Map();
      
      seriesData.forEach((item) => {
        const levelValue = item.breakdownValues[levelIndex] || 'Overall';
        
        if (!groups.has(levelValue)) {
          groups.set(levelValue, {
            label: levelValue,
            items: []
          });
        }
        
        groups.get(levelValue).items.push(item);
      });

      // Recursively build children for each group
      const result = [];
      groups.forEach((group) => {
        const children = buildHierarchicalTree(group.items, levelIndex + 1, maxLevel);
        
        // Recompute total from items (not incremental sum) to avoid double counting
        const total = group.items.reduce((sum, item) => {
          return sum + (item.summary?.total ?? (item.data || []).reduce((s, point) => s + (point.value || 0), 0));
        }, 0);
        
        result.push({
          label: group.label,
          total: total,
          children: levelIndex < maxLevel - 1 ? children : undefined, // Only nest if not at leaf level
          bars: levelIndex === maxLevel - 1 ? children : undefined   // Bars only at last level
        });
      });

      return result;
    };

    // Build hierarchical tree structure for nested breakdowns
    if (hasHierarchicalBreakdowns && isHorizontalBar) {
      // breakdownDepth already extracted above (line 835)
      const isStackedBar = normalizedChartType === "stacked-bar";
      
      // Number of breakdown columns to render (layout math fix)
      const visibleBreakdownLevels = isStackedBar
        ? Math.max(breakdownDepth - 1, 0)  // stacked bars: last level becomes segments
        : breakdownDepth;                    // regular bars: show all levels
      
      // Group by metric first
      const metricGroups = new Map();
      
      series.forEach((metricSeries) => {
        const metricId = metricSeries.metricId || 'default';
        
        if (!metricGroups.has(metricId)) {
          metricGroups.set(metricId, {
            metricId,
            metricName: metricSeries.metricBaseName || metricSeries.fullMetricName || metricSeries.metricName || `Metric ${String.fromCharCode(65 + metricGroups.size)}`,
            items: []
          });
        }
        
        metricGroups.get(metricId).items.push(metricSeries);
      });
      
      // Get breakdown dimension names from series
      // Try multiple sources: breakdownDimensionNames, breakdownProperties, or derive from breakdown structure
      let breakdownDimensionNames = null;
      if (series.length > 0) {
        // First try: direct breakdownDimensionNames
        if (series[0].breakdownDimensionNames && Array.isArray(series[0].breakdownDimensionNames)) {
          breakdownDimensionNames = series[0].breakdownDimensionNames;
        }
        // Second try: extract from breakdownProperties (has propertyLabel)
        else if (series[0].breakdownProperties && Array.isArray(series[0].breakdownProperties)) {
          breakdownDimensionNames = series[0].breakdownProperties.map(p => p.propertyLabel || p.property || 'Dimension');
        }
        // Third try: check if data object has breakdownProperties
        else if (data?.breakdownProperties && Array.isArray(data.breakdownProperties)) {
          breakdownDimensionNames = data.breakdownProperties.map(p => p.propertyLabel || p.property || 'Dimension');
        }
      }
      
      // Build hierarchical tree for each metric (starting from level 0)
      const hierarchicalData = new Map();
      metricGroups.forEach((metricGroup, metricId) => {
        hierarchicalData.set(metricId, {
          metricName: metricGroup.metricName,
          tree: buildHierarchicalTree(metricGroup.items, 0, breakdownDepth),
          breakdownDimensionNames: breakdownDimensionNames // Store breakdown names for use in headers
        });
      });
      
      // Build color map for LAST breakdown dimension values (order of first appearance, not alphabetical)
      const lastLevelValueArray = [];
      const seenValues = new Set();
      series.forEach((item) => {
        const lastValue = item.breakdownValues[item.breakdownValues.length - 1];
        if (!seenValues.has(lastValue)) {
          seenValues.add(lastValue);
          lastLevelValueArray.push(lastValue);
        }
      });
      
      const barColorMap = new Map();
      lastLevelValueArray.forEach((value, index) => {
        barColorMap.set(value, getSeriesColor(index));
      });
      
      // Flatten hierarchical tree into rows for column-based rendering
      // For stacked bars: stop at second-to-last level and collect children as segments
      // For regular bars: each row represents one complete path to leaf
      const flattenTreeToRows = (tree, currentPath = [], rows = [], levelIndex = 0) => {
        // Sort tree nodes by total (descending) to match table ordering
        const sortedTree = [...tree].sort((a, b) => b.total - a.total);
        
        // ⭐ SINGLE BREAKDOWN STACKED BAR - collect ALL nodes' bars into ONE row
        if (isStackedBar && breakdownDepth === 1 && levelIndex === 0 && currentPath.length === 0) {
          const allBars = [];
          sortedTree.forEach((node) => {
            if (node.bars?.length > 0) {
              allBars.push(...node.bars.map(bar => ({
                label: bar.label,
                value: bar.value ?? bar.total ?? 0,
                series: bar.series
              })));
            }
          });
          
          if (allBars.length > 0) {
            rows.push({
              path: [], // Empty path for single breakdown (no breakdown column)
              bars: allBars
            });
          }
          return rows;
        }
        
        sortedTree.forEach((node) => {
          const newPath = [...currentPath, { label: node.label, total: node.total }];
          
          if (isStackedBar) {
            // ⭐ MULTI BREAKDOWN (existing logic — keep)
            if (
              breakdownDepth >= 2 &&
              levelIndex === breakdownDepth - 2 &&
              node.children?.length > 0
            ) {
              const bars = [...node.children].map(child => ({
                label: child.label,
                value: child.total ?? child.value ?? 0,
                series: child.series
              }));

              rows.push({
                path: newPath,
                bars
              });

              return;
            }

            if (node.children) {
              // Not at target level yet - recurse with incremented levelIndex
              flattenTreeToRows(node.children, newPath, rows, levelIndex + 1);
            }
          } else {
            // For regular bars: existing behavior - create row for each leaf
            if (node.bars) {
              // Leaf node - add row with bars
              rows.push({
                path: newPath,
                bars: node.bars
              });
            } else if (node.children) {
              // Parent node - recurse into children
              flattenTreeToRows(node.children, newPath, rows, levelIndex + 1);
            }
          }
        });
        
        return rows;
      };
      
      // Pre-calculate rowSpans for all levels - FIXED LOGIC
      const calculateRowSpans = (rows) => {
        // FIX #3: Performance guard - early return for empty rows
        if (!rows.length) return [];
        
        const rowSpans = rows.map(() => []);
        // Calculate spans for all breakdown levels in the path
        // For stacked bars: path length is breakdownDepth - 1 (stopped at breakdownDepth - 2)
        // For regular bars: path length is breakdownDepth - 1 (stopped at breakdownDepth - 1, but bars are separate)
        const maxLevelIndex = visibleBreakdownLevels;
        
        for (let levelIndex = 0; levelIndex < maxLevelIndex; levelIndex++) {
          for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
            const row = rows[rowIndex];
            const prevRow = rowIndex > 0 ? rows[rowIndex - 1] : null;
            
            // FIX #1: Correct isNewGroup detection - check ALL levels first
            let isNewGroup = !prevRow;
            if (prevRow) {
              // Check if ANY level from 0 to levelIndex differs
              for (let i = 0; i <= levelIndex; i++) {
                if (row.path[i]?.label !== prevRow.path[i]?.label) {
                  isNewGroup = true;
                  break;
                }
              }
            }
            
            if (isNewGroup) {
              // Calculate how many consecutive rows share the same path up to this level
              let span = 1;
              for (let nextIdx = rowIndex + 1; nextIdx < rows.length; nextIdx++) {
                let allMatch = true;
                for (let j = 0; j <= levelIndex; j++) {
                  if (rows[nextIdx].path[j]?.label !== row.path[j]?.label) {
                    allMatch = false;
                    break;
                  }
                }
                if (allMatch) {
                  span++;
                } else {
                  break;
                }
              }
              rowSpans[rowIndex][levelIndex] = span;
            } else {
              rowSpans[rowIndex][levelIndex] = 0; // Spanned from previous row
            }
          }
        }
        
        return rowSpans;
      };
      
      // Custom hierarchical bar chart component - STRICT ROW-BASED LAYOUT
      const HierarchicalBarChart = () => {
        // State for custom tooltip
        const [hoveredSegment, setHoveredSegment] = React.useState(null);
        const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 });
        
        // Memoize rows per metric to avoid duplicate flattening
        const rowsByMetric = React.useMemo(() => {
          const map = new Map();
          
          hierarchicalData.forEach((metricData, key) => {
            map.set(metricData.metricName, flattenTreeToRows(metricData.tree));
          });
          
          return map;
        }, [hierarchicalData, isStackedBar, breakdownDepth]);
        
        // Calculate all rows for global max calculation
        const allRows = React.useMemo(() => {
          return Array.from(rowsByMetric.values()).flat();
        }, [rowsByMetric]);
        
        // Calculate ONE global maximum value across ALL rows (Mixpanel-style)
        const globalMaxValue = React.useMemo(() => {
          if (!allRows.length) return 0;
          
          if (isStackedBar) {
            // For stacked bars: compare TOTAL of each stacked row
            return Math.max(
              ...allRows.map(r =>
                (r.bars || []).reduce((sum, b) => sum + (b.value || 0), 0)
              )
            );
          }
          
          // For regular bars: compare individual bar values
          return Math.max(
            ...allRows.flatMap(r => (r.bars || []).map(b => b.value || 0))
          );
        }, [allRows, isStackedBar]);
        
        return (
          <div ref={chartContainerRef} className="w-full overflow-auto" style={{ height, fontFamily: "Raleway, sans-serif" }}>
            {Array.from(hierarchicalData.values()).map((metricData, metricIndex) => {
              // Get pre-computed rows from memoized map (no duplicate flattening)
              const rows = rowsByMetric.get(metricData.metricName) || [];
              
              // Pre-calculate all rowSpans for proper alignment
              const rowSpans = calculateRowSpans(rows);
              
              // Get breakdown dimension names for this metric
              const metricBreakdownNames = metricData.breakdownDimensionNames || breakdownDimensionNames;
              
              // Column offset for metric column (only in stacked bar)
              const columnOffset = isStackedBar ? 1 : 0;
              
              return (
                <div key={metricData.metricName} style={{ marginBottom: metricIndex < hierarchicalData.size - 1 ? '40px' : 0 }}>
                  {/* Metric header - always show metric name */}
                  <div className="mb-4" style={{ 
                    color: theme.textPrimary, 
                    fontWeight: 600,
                    fontSize: '14px',
                    paddingLeft: '8px',
                    marginBottom: '20px'
                  }}>
                    {metricData.metricName}
                  </div>
                  
                  {/* TRUE HTML TABLE STRUCTURE - Mixpanel-exact */}
                  <table style={{ 
                    width: '100%',
                    borderCollapse: 'collapse',
                    backgroundColor: theme.bgRaised || '#ffffff',
                    borderRadius: '8px',
                    border: `1px solid ${theme.border || '#e5e7eb'}`,
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}>
                    {/* Header row */}
                    <thead style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      backgroundColor: theme.bgOverlay || '#f9fafb'
                    }}>
                      <tr style={{
                        borderBottom: `2px solid ${theme.border || '#e5e7eb'}`
                      }}>
                        {/* Metric column - only for stacked bar */}
                        {isStackedBar && (
                          <th style={{
                            width: '200px',
                            padding: '12px 16px',
                            borderRight: `1px solid ${theme.border || '#e5e7eb'}`,
                            fontSize: '12px',
                            fontWeight: 600,
                            color: theme.textMuted || theme.textSecondary,
                            textAlign: 'left',
                            backgroundColor: theme.bgOverlay || '#f9fafb'
                          }}>
                            Metric
                          </th>
                        )}
                        {/* Breakdown dimension columns - exclude last level for stacked bars (show all except last) */}
                        {Array.from({ length: visibleBreakdownLevels }).map((_, levelIndex) => (
                          <th
                            key={`header-${levelIndex}`}
                            style={{
                              width: levelIndex === 0 ? '220px' : '200px',
                              padding: '12px 16px',
                              borderRight: `1px solid ${theme.border || '#e5e7eb'}`, // Vertical line after each breakdown
                              fontSize: '12px',
                              fontWeight: 600,
                              color: theme.textMuted || theme.textSecondary,
                              textAlign: 'left',
                              backgroundColor: theme.bgOverlay || '#f9fafb'
                            }}
                          >
                            {metricBreakdownNames?.[levelIndex] || `Level ${levelIndex + 1}`}
                          </th>
                        ))}
                        {/* Value column header */}
                        <th style={{
                          padding: '12px 16px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: theme.textMuted || theme.textSecondary,
                          textAlign: 'left',
                          backgroundColor: theme.bgOverlay || '#f9fafb'
                        }}>
                          {isStackedBar ? 'Value' : 'Bars'}
                        </th>
                      </tr>
                    </thead>
                    
                    {/* Data rows - TRUE TABLE ROWS */}
                    <tbody>
                      {rows.map((row, rowIndex) => {
                        const prevRow = rowIndex > 0 ? rows[rowIndex - 1] : null;
                        const isNewCountry = rowIndex === 0 || row.path[0]?.label !== prevRow?.path[0]?.label;
                        
                        // Compute rowTotal once per row (for stacked bars - used in tooltip percentage)
                        const rowTotal = isStackedBar && row.bars 
                          ? row.bars.reduce((sum, b) => sum + b.value, 0)
                          : 0;
                        
                        // Use globalMaxValue for all bar scaling (Mixpanel-style)
                        const maxBarValue = globalMaxValue;
                        
                        return (
                          <tr
                            key={`row-${rowIndex}`}
                            style={{
                              borderBottom: `1px solid ${theme.border || '#e5e7eb'}`,
                              borderTop: isNewCountry ? `2px solid ${theme.border || '#e5e7eb'}` : 'none',
                              backgroundColor: 'transparent',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = theme.bgOverlay || '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {/* FIX #1: Metric column - only render on first row, rowSpan = all rows for this metric */}
                            {isStackedBar && rowIndex === 0 && (
                              <td
                                rowSpan={rows.length}
                                style={{
                                  width: '200px',
                                  padding: '12px 16px',
                                  borderRight: `1px solid ${theme.border || '#e5e7eb'}`,
                                  verticalAlign: 'top',
                                  fontWeight: 500,
                                  color: theme.textPrimary
                                }}
                              >
                                {metricData.metricName}
                              </td>
                            )}
                            
                            {/* Render breakdown level columns - show all breakdowns except last (which becomes segments) */}
                            {Array.from({ length: visibleBreakdownLevels }).map((_, levelIndex) => {
                              const span = rowSpans[rowIndex][levelIndex];
                              const levelValue = row.path[levelIndex];
                              
                              // Only render cell if this is the first row of a group (span > 0)
                              // When span === 0, we skip rendering - table automatically handles alignment
                              if (span === 0) {
                                return null; // Real table handles missing cells automatically
                              }
                              
                              return (
                                <td
                                  key={`cell-${levelIndex}-${rowIndex}`}
                                  rowSpan={span > 1 ? span : undefined}
                                  style={{
                                    width: levelIndex === 0 ? '220px' : '200px',
                                    padding: '12px 16px',
                                    borderRight: `1px solid ${theme.border || '#e5e7eb'}`, // Vertical line after each breakdown
                                    verticalAlign: 'top'
                                  }}
                                >
                                  <div style={{
                                    fontSize: '13px',
                                    fontWeight: levelIndex === 0 ? 600 : 500,
                                    color: theme.textPrimary,
                                    marginBottom: '6px'
                                  }}>
                              {!levelValue.label || String(levelValue.label).trim() === '' || levelValue.label === '(none)' ? '(not set)' : levelValue.label}
                                  </div>
                                  <div style={{
                                    fontSize: '11px',
                                    color: theme.textSecondary,
                                    fontWeight: 400
                                  }}>
                                    Total: {formatValue(levelValue.total)}
                                  </div>
                                </td>
                              );
                            })}
                            
                            {/* Value/Bars column - TRUE TABLE CELL */}
                            <td style={{
                              padding: '12px 16px',
                              verticalAlign: 'top'
                            }}>
                              {row.bars && row.bars.length > 0 ? (
                                isStackedBar ? (
                                  // STACKED BAR RENDERING
                                  <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    height: '32px'
                                  }}>
                                    {/* Stacked bar container - scale width relative to rowTotal vs globalMax */}
                                    <div style={{ 
                                      flex: 1,
                                      minWidth: 0,
                                      height: '28px',
                                      backgroundColor: theme.bgOverlay || '#f3f4f6',
                                      borderRadius: '4px',
                                      overflow: 'hidden',
                                      position: 'relative',
                                      display: 'flex',
                                      width: maxBarValue > 0 ? `${(rowTotal / maxBarValue) * 100}%` : '0%', // Scale container to rowTotal
                                      boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
                                    }}>
                                      {/* Render segments - each segment fills container relative to rowTotal */}
                                      {row.bars.map((bar, barIndex) => {
                                        const barColor = barColorMap.get(bar.label);
                                        
                                        // Scale segment width relative to rowTotal (so segments fill container completely)
                                        const segmentWidth = rowTotal > 0 ? (bar.value / rowTotal) * 100 : 0;
                                        
                                        // Build full breakdown path for tooltip
                                        const fullPath = [...row.path.map(p => p.label), bar.label].join(' / ');
                                        
                                        // Percentage should be relative to rowTotal (not globalMax) for tooltip
                                        const percentage = rowTotal > 0 ? ((bar.value / rowTotal) * 100).toFixed(2) : '0.00';
                                        
                                        const handleMouseEnter = (e) => {
                                          setHoveredSegment({
                                            metricName: metricData.metricName,
                                            breakdownValue: bar.label,
                                            value: bar.value,
                                            fullPath,
                                            percentage,
                                            barColor
                                          });
                                          // Initial position from mouse event
                                          setTooltipPosition({
                                            x: e.clientX,
                                            y: e.clientY
                                          });
                                        };
                                        
                                        const handleMouseMove = (e) => {
                                          // Update tooltip position to follow cursor
                                          setTooltipPosition({
                                            x: e.clientX,
                                            y: e.clientY
                                          });
                                        };
                                        
                                        const handleMouseLeave = () => {
                                          setHoveredSegment(null);
                                        };
                                        
                                        return (
                                          <div
                                            key={`stacked-segment-${rowIndex}-${barIndex}`}
                                            style={{
                                              width: `${segmentWidth}%`, // Segment width relative to rowTotal (fills container)
                                              height: '100%',
                                              backgroundColor: barColor,
                                              transition: 'width 0.3s ease',
                                              boxShadow: barIndex === 0 ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                                              borderRight: barIndex < row.bars.length - 1 ? `1px solid rgba(255, 255, 255, 0.3)` : 'none',
                                              cursor: 'pointer'
                                            }}
                                            onMouseEnter={handleMouseEnter}
                                            onMouseMove={handleMouseMove}
                                            onMouseLeave={handleMouseLeave}
                                          />
                                        );
                                      })}
                                    </div>
                                    
                                    {/* Total value display - use pre-computed rowTotal */}
                                    <div style={{ 
                                      minWidth: '70px',
                                      textAlign: 'right',
                                      fontSize: '12px',
                                      color: theme.textPrimary,
                                      fontWeight: 600
                                    }}>
                                      {formatValue(rowTotal)}
                                    </div>
                                  </div>
                                ) : (
                                  // REGULAR BAR RENDERING
                                  // For hierarchical tables with breakdown columns, don't show bar labels (they're in breakdown columns)
                                  // For flat tables without breakdown columns, show bar labels
                                  row.bars
                                    .sort((a, b) => b.value - a.value)
                                    .map((bar, barIndex) => {
                                      const barWidth = maxBarValue > 0 ? (bar.value / maxBarValue) * 100 : 0;
                                      const barColor = barColorMap.get(bar.label);
                                      const showBarLabel = visibleBreakdownLevels === 0; // Only show label if no breakdown columns
                                      
                                      return (
                                        <div 
                                          key={`bar-${rowIndex}-${barIndex}`}
                                          style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            height: '28px',
                                            marginBottom: barIndex < row.bars.length - 1 ? '6px' : '0'
                                          }}
                                        >
                                          {showBarLabel && (
                                            <div style={{ 
                                              minWidth: '120px',
                                              maxWidth: '180px',
                                              fontSize: '12px',
                                              color: theme.textPrimary,
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              fontWeight: 500
                                            }}>
                                              {bar.label}
                                            </div>
                                          )}
                                          
                                          <div style={{ 
                                            flex: 1,
                                            minWidth: 0,
                                            height: '24px',
                                            backgroundColor: theme.bgOverlay || '#f3f4f6',
                                            borderRadius: '4px',
                                            overflow: 'hidden',
                                            position: 'relative',
                                            boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
                                          }}>
                                            <div style={{
                                              width: `${barWidth}%`,
                                              height: '100%',
                                              backgroundColor: barColor,
                                              borderRadius: '4px',
                                              transition: 'width 0.3s ease',
                                              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                                            }} />
                                          </div>
                                          
                                          <div style={{ 
                                            minWidth: '70px',
                                            textAlign: 'right',
                                            fontSize: '12px',
                                            color: theme.textPrimary,
                                            fontWeight: 600
                                          }}>
                                            {formatValue(bar.value)}
                                          </div>
                                        </div>
                                      );
                                    })
                                )
                              ) : (
                                <div style={{ 
                                  fontSize: '12px',
                                  color: theme.textMuted,
                                  fontStyle: 'italic',
                                  padding: '8px 0'
                                }}>
                                  No data
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
            
            {/* Legend for bar colors - hidden (legends only on pie chart) */}
            {isStackedBar && showChartLegend && lastLevelValueArray.length > 0 && (
              <div style={{ 
                marginTop: '20px',
                padding: '12px 16px',
                backgroundColor: theme.bgOverlay || '#f9fafb',
                borderRadius: '8px',
                border: `1px solid ${theme.border || '#e5e7eb'}`,
                display: 'flex',
                flexWrap: 'wrap',
                gap: '20px'
              }}>
                {lastLevelValueArray.map((label) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '3px',
                      backgroundColor: barColorMap.get(label),
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                    }} />
                    <span style={{ fontSize: '12px', color: theme.textSecondary, fontWeight: 500 }}>{label}</span>
                  </div>
                ))}
              </div>
            )}
            
            {/* Custom Tooltip - Card Style (portaled to body so it escapes chart overflow) */}
            {hoveredSegment && ReactDOM.createPortal(
              <div
                className="fixed pointer-events-none z-[9999]"
                style={{
                  left: `${tooltipPosition.x + 10}px`,
                  top: `${tooltipPosition.y - 10}px`,
                  fontFamily: "Raleway, sans-serif"
                }}
              >
                <div
                  style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${hoveredSegment.barColor || theme.primaryLight}`,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '16px 20px',
                    minWidth: '200px',
                    textAlign: 'center'
                  }}
                >
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: theme.textPrimary || '#111827',
                    marginBottom: '4px',
                    lineHeight: '1.4'
                  }}>
                    {hoveredSegment.metricName}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: theme.textSecondary || '#6B7280',
                    marginBottom: '8px'
                  }}>
                    {hoveredSegment.fullPath || hoveredSegment.breakdownValue}
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: theme.textPrimary || '#111827'
                  }}>
                    {formatValue(hoveredSegment.value)} events
                  </div>
                </div>
              </div>,
              document.body
            )}
          </div>
        );
      };
      
      return <HierarchicalBarChart />;
    }
    
    // Original bar chart rendering (no hierarchical breakdowns)
    return (
      <div ref={chartContainerRef} className="w-full" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={displayData} 
            margin={chartMargin}
            layout="vertical"
          >
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} horizontal={false} />
            <XAxis type="number" hide={!leftYVisible} tick={{ fill: tickFill, fontSize: 11 }} axisLine={{ stroke: axisStroke }} tickLine={false} tickFormatter={formatValue} domain={leftYDomain} scale={leftYScale} allowDataOverflow={!outOfBoundsLeft} {...(leftYLabel ? { label: { value: leftYLabel, position: "insideBottom", offset: 24, style: axisLabelStyle } } : {})} />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: tickFill, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={200}
              hide={true}
            />
            {thresholdLines.map((line, i) => (
              <ReferenceLine key={`threshold-${i}`} x={line.value} stroke={line.stroke} strokeDasharray={line.strokeDasharray} strokeWidth={line.strokeWidth} />
            ))}
            <Tooltip content={portalTooltipContent} cursor={{ fill: `${theme.primaryLight}20` }} />
            {showChartLegend && (
              <Legend
                verticalAlign="top"
                align="center"
                layout="horizontal"
                wrapperStyle={{ paddingBottom: 8 }}
                formatter={(value) => {
                  const config = seriesConfig.find(s => s.key === value);
                  return config?.name || value;
                }}
              />
            )}
            {seriesConfig.map((config) => (
              <Bar 
                key={config.key}
                dataKey={config.key} 
                fill={config.color} 
                radius={[0, 4, 4, 0]}
                name={config.name}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Stacked Bar Chart (horizontal stacked) - shows aggregated totals, one stacked bar with all metrics
  if (normalizedChartType === "stacked-bar") {
    return (
      <div ref={chartContainerRef} className="w-full" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={displayData} 
            margin={chartMargin}
            layout="vertical"
          >
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} horizontal={false} />
            <XAxis type="number" hide={!leftYVisible} tick={{ fill: tickFill, fontSize: 11 }} axisLine={{ stroke: axisStroke }} tickLine={false} tickFormatter={formatValue} domain={leftYDomain} scale={leftYScale} allowDataOverflow={!outOfBoundsLeft} {...(leftYLabel ? { label: { value: leftYLabel, position: "insideBottom", offset: 24, style: axisLabelStyle } } : {})} />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: tickFill, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={200}
              hide={true}
            />
            {thresholdLines.map((line, i) => (
              <ReferenceLine key={`threshold-${i}`} x={line.value} stroke={line.stroke} strokeDasharray={line.strokeDasharray} strokeWidth={line.strokeWidth} />
            ))}
            <Tooltip content={portalTooltipContent} cursor={{ fill: `${theme.primaryLight}20` }} />
            {showChartLegend && (
              <Legend
                verticalAlign="top"
                align="center"
                layout="horizontal"
                wrapperStyle={{ paddingBottom: 8 }}
                formatter={(value) => {
                  const config = seriesConfig.find(s => s.key === value);
                  return config?.name || value;
                }}
              />
            )}
            {seriesConfig.map((config, idx) => (
              <Bar 
                key={config.key}
                dataKey={config.key} 
                stackId="stack"
                fill={config.color} 
                radius={idx === seriesConfig.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]}
                name={config.name}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Pie Chart
  if (normalizedChartType === "pie") {
    // For pie chart with multiple metrics, show total for each metric
    let pieData;
    if (isMultiSeries) {
      pieData = seriesConfig.map((config) => {
        const total = (config.data || []).reduce((sum, item) => sum + (item.value || 0), 0);
        return {
          name: config.name,
          value: total,
        };
      });
    } else {
      // Legacy: show the last N data points
      pieData = chartData.slice(-10).map((item, index) => ({
        name: formatDate(item.date) || `Item ${index + 1}`,
        value: item.value || 0,
      }));
    }

    const total = pieData.reduce((sum, item) => sum + item.value, 0);

    // Legend: max 7 visible, "+X More" with click-to-open dropdown for rest
    const LEGEND_MAX_VISIBLE = 7;
    const visibleLegendItems = pieData.slice(0, LEGEND_MAX_VISIBLE);
    const overflowLegendItems = pieData.slice(LEGEND_MAX_VISIBLE);

    return (
      <div ref={chartContainerRef} className="w-full flex flex-col" style={{ height }}>
        {/* Custom legend: max 7 in row + "+X More" with click-to-open dropdown */}
        {showLegend && pieData.length > 0 && (
          <div
            className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pb-3 flex-shrink-0 relative"
            style={{ minHeight: 32 }}
          >
            {visibleLegendItems.map((entry, index) => (
              <div key={`${entry.name}-${index}`} className="flex items-center gap-1.5 flex-shrink-0">
                <div
                  className="w-3 h-3 rounded flex-shrink-0"
                  style={{ backgroundColor: getSeriesColor(index) }}
                />
                <span
                  className="text-xs truncate max-w-[140px]"
                  style={{ color: theme.textSecondary }}
                  title={entry.name}
                >
                  {entry.name}
                </span>
              </div>
            ))}
            {overflowLegendItems.length > 0 && (
              <div
                ref={pieLegendMoreRef}
                className="relative flex-shrink-0"
                onClick={() => setShowPieLegendMore((prev) => !prev)}
              >
                <span
                  className="text-xs cursor-pointer"
                  style={{ color: theme.primaryLight || "#4B57AA", fontWeight: 500 }}
                >
                  +{overflowLegendItems.length} More
                </span>
                {showPieLegendMore && (
                  <div
                    className="absolute right-0 top-full mt-1 z-50 py-2 px-1 rounded-lg border shadow-lg overflow-y-auto"
                    style={{
                      maxHeight: 280,
                      minWidth: 200,
                      maxWidth: "min(300px, calc(100vw - 2rem))",
                      backgroundColor: theme.bgRaised || "#ffffff",
                      borderColor: theme.border || "#E5E7EB",
                    }}
                  >
                    {overflowLegendItems.map((entry, index) => (
                      <div
                        key={`overflow-${entry.name}-${index}`}
                        className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-black/5"
                      >
                        <div
                          className="w-3 h-3 rounded flex-shrink-0"
                          style={{ backgroundColor: getSeriesColor(LEGEND_MAX_VISIBLE + index) }}
                        />
                        <span
                          className="text-xs truncate flex-1"
                          style={{ color: theme.textPrimary }}
                          title={entry.name}
                        >
                          {entry.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={false}
                outerRadius={80}
                fill={theme.accentChart}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getSeriesColor(index)} />
                ))}
              </Pie>
              <Tooltip
                content={makePortalTooltip(({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const data = payload[0];
                  const percent = ((data.value / total) * 100).toFixed(1);
                  return (
                    <div
                      className="px-3 py-2 rounded border shadow-sm text-sm"
                      style={{
                        backgroundColor: theme.bgRaised,
                        borderColor: theme.border,
                        color: theme.textPrimary,
                      }}
                    >
                      <div className="mb-1" style={{ color: theme.textMuted }}>{data.name}</div>
                      <div className="font-medium">{formatCompactNumber(data.value)}</div>
                      <div className="text-xs mt-0.5" style={{ color: theme.textMuted }}>
                        {percent}% of total
                      </div>
                    </div>
                  );
                })}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // Table View - shows one row per metric with total aggregated values
  if (normalizedChartType === "table") {
    return (
      <div className="w-full" style={{ height: "auto", maxHeight: height }}>
        <div className="overflow-auto" style={{ maxHeight: height - 40 }}>
          <table className="w-full text-sm" style={{ fontFamily: "Raleway, sans-serif" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}`, backgroundColor: theme.bgOverlay }}>
                <th className="text-left py-3 px-4" style={{ color: theme.textMuted, fontWeight: 600 }}>
                  Metric
                </th>
                <th className="text-right py-3 px-4" style={{ color: theme.textMuted, fontWeight: 600 }}>
                  Total Events
                </th>
              </tr>
            </thead>
            <tbody>
              {seriesConfig.map((config, index) => (
                <tr
                  key={config.key}
                  style={{
                    borderBottom: `1px solid ${theme.border}`,
                    backgroundColor: index % 2 === 0 ? "transparent" : theme.bgOverlay,
                  }}
                >
                  <td className="py-3 px-4" style={{ color: theme.textPrimary }}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: config.color }}
                      />
                      <span>{config.name}</span>
                    </div>
                  </td>
                  <td className="text-right py-3 px-4" style={{ color: theme.textPrimary, fontWeight: 500 }}>
                    {config.total != null ? formatCompactNumber(config.total) : "0"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Metric View
  if (normalizedChartType === "metric") {
    return (
      <div className="flex flex-col items-center justify-center h-[200px] text-center">
        <div className="text-4xl font-bold" style={{ color: theme.textPrimary }}>
          {summary?.avg != null ? formatCompactNumber(summary.avg) : summary?.total != null ? formatCompactNumber(summary.total) : "0"}
        </div>
        <div className="text-sm mt-1" style={{ color: theme.textMuted }}>
          {summary?.metric ?? "Metric"}
        </div>
        {summary?.delta && (
          <div className="text-sm mt-1" style={{ color: theme.accentPositive }}>
            {summary.delta}
          </div>
        )}
      </div>
    );
  }

  // Default: Line Chart
  return (
    <div ref={chartContainerRef} className="relative w-full" style={{ height, margin: 0, padding: 0 }}>
      {YAxisLabelOverlay}
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart data={displayData} margin={chartMargin}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fill: tickFill, fontSize: 11 }}
            axisLine={{ stroke: axisStroke }}
            tickLine={false}
            tickFormatter={formatDate}
            hide={!xAxisVisible}
            {...xAxisLabelProps}
          />
          <YAxis
            yAxisId="left"
            hide={!leftYVisible}
            tick={leftYAxisTick}
            axisLine={false}
            tickLine={false}
            width={leftYVisible ? yAxisWidth : 0}
            tickFormatter={formatValue}
            domain={leftYDomain}
            scale={leftYScale}
            allowDataOverflow={!outOfBoundsLeft}
            {...yAxisLabelProps}
          />
          {rightYVisible && (
            <YAxis
              yAxisId="right"
              orientation="right"
              hide={false}
              tick={rightYAxisTick}
              axisLine={false}
              tickLine={false}
              width={yAxisWidth}
              tickFormatter={formatValueRight}
              domain={rightYDomain}
              scale={rightYScale}
              allowDataOverflow={!outOfBoundsRight}
              {...rightYAxisLabelProps}
            />
          )}
          {visibleThresholdLines.map((line, i) => (
            <ReferenceLine key={`threshold-${i}`} yAxisId={line.yAxisId || "left"} y={line.value} stroke={line.stroke} strokeDasharray={line.strokeDasharray} strokeWidth={line.strokeWidth} />
          ))}
          <Tooltip content={portalTooltipContent} cursor={{ stroke: theme.accentChart, strokeWidth: 1 }} />
          {showChartLegend && (
            <Legend 
              formatter={(value) => {
                const sc = seriesConfig.find(s => s.key === value);
                return sc?.name || value;
              }}
            />
          )}
          {seriesConfig.map((config) => (
            <Line
              key={config.key}
              yAxisId={config.axis === "right" ? "right" : "left"}
              type="monotone"
              dataKey={config.key}
              stroke={config.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: config.color }}
              name={config.name}
              connectNulls={config.axis === "right" ? connectNullsRight : connectNullsLeft}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
}
